import { motion } from 'framer-motion';
import { Check, Zap, Star, TrendingUp, Play, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ROUTE_PATHS } from '@/lib/index';
import { springPresets } from '@/lib/motion';

export default function Pricing() {
  const plans = [
    {
      id: 'credit_builder',
      name: 'Credit Builder',
      tagline: 'Foundation - Credit repair only',
      monthlyPrice: 197,
      annualPrice: 2067,
      setupFee: 149,
      popular: false,
      demoPath: '/demo/credit-builder',
      features: [
        'Unlimited credit disputes (all 3 bureaus)',
        'AI dispute letter factory',
        'Real-time score tracking',
        'Basic credit education (6 lessons)',
        'Email support (48hr response)',
        'Monthly progress reports',
      ],
      notIncluded: ['Tax strategy', 'Business funding', 'Goal roadmaps', 'Advanced education', '1-on-1 coaching'],
    },
    {
      id: 'wealth_accelerator',
      name: 'Wealth Accelerator',
      tagline: 'Most Popular - Credit + Tax + Funding',
      monthlyPrice: 397,
      annualPrice: 4167,
      setupFee: 149,
      popular: true,
      demoPath: '/demo/wealth-accelerator',
      features: [
        'Everything in Credit Builder',
        'AI tax deduction scanner (avg $47k found)',
        'Tax strategy optimizer',
        'Entity structure consulting',
        'Business funding matches ($50k-$150k)',
        'Goal roadmap system (house, car, business)',
        'Advanced credit education (12 modules)',
        'Priority email support (24hr response)',
        'Bi-weekly strategy calls',
      ],
      notIncluded: ['1-on-1 coaching', 'Custom tax planning', 'Phone support'],
    },
    {
      id: 'business_empire',
      name: 'Business Empire',
      tagline: 'For Entrepreneurs - Full business suite',
      monthlyPrice: 697,
      annualPrice: 7317,
      setupFee: 149,
      popular: false,
      demoPath: '/demo/business-empire',
      features: [
        'Everything in Wealth Accelerator',
        'NAICS code optimizer',
        'Tier-1 trade line access',
        '0% funding strategy ($100k-$250k)',
        'Advanced tax strategies (Augusta Rule, S-Corp)',
        'Business credit building roadmap',
        'Complete credit education library (20+ modules)',
        'Phone + email support',
        'Weekly strategy calls',
        'Quarterly business review',
      ],
      notIncluded: ['1-on-1 coaching', 'Custom CPA review'],
    },
    {
      id: 'millionaire_mentorship',
      name: 'Millionaire Mentorship',
      tagline: 'Elite - Personal coaching + full service',
      monthlyPrice: 1497,
      annualPrice: 15717,
      setupFee: 0,
      popular: false,
      demoPath: '/demo/millionaire-mentorship',
      features: [
        'Everything in Business Empire',
        '1-on-1 coaching (4 sessions/month)',
        'Custom credit strategies',
        'Custom tax planning with CPA review',
        'Custom funding roadmap',
        'Direct phone/text access',
        'Dedicated account manager',
        'Same-day support',
        'Setup fee waived ($149 value)',
        'Monthly financial review',
        'Exclusive mastermind group access',
      ],
      notIncluded: [],
    },
  ];

  const faqs = [
    {
      q: 'Why is Credit Builder $197/mo when competitors charge $139-$149?',
      a: 'Because we include unlimited disputes (competitors limit to 5-10/month), AI letter generation (saves you hours), real-time 3-bureau tracking, and 6 education modules. Competitors charge extra for these. You\'re getting 3x more value.',
    },
    {
      q: 'What makes Wealth Accelerator worth $397/mo?',
      a: 'Our AI tax scanner finds an average of $47,200 in deductions. That alone pays for 10 YEARS of service. Plus you get business funding access ($50k-$150k), goal roadmaps, and advanced education. If you hired a CPA ($2,000/year) + credit repair ($1,788/year) + business consultant ($5,000/month), you\'d pay $62,788/year. We charge $4,764/year. You save $58,024.',
    },
    {
      q: 'Is the credit education really worth the price difference?',
      a: 'Absolutely. Our education library includes insider strategies that credit repair companies charge $500-$2,000 for separately. You learn how to maintain 800+ scores, leverage credit for wealth building, and avoid mistakes that cost thousands. Knowledge is the real wealth multiplier.',
    },
    {
      q: 'Can I see what each package includes before buying?',
      a: 'Yes! Click "View Demo" on any package to see exactly what you get. We also offer a 90-day money-back guarantee. If you don\'t see value, we refund everything.',
    },
    {
      q: 'What if I only need credit repair right now?',
      a: 'Start with Credit Builder ($197/mo). But 87% of our clients upgrade to Wealth Accelerator within 60 days once they see the tax savings and funding opportunities. Most people don\'t realize they\'re leaving $50k+ on the table.',
    },
    {
      q: 'How long does credit repair take?',
      a: 'Most clients see results in 45-90 days. Average score increase is +94 points over 6 months. But the real transformation happens when you combine credit repair with tax optimization and funding access - that\'s when you go from "fixing credit" to "building wealth."',
    },
    {
      q: 'Is my data secure?',
      a: 'Yes. We use bank-level encryption (256-bit SSL), secure cloud infrastructure, and comply with all financial data protection regulations. Your information is never shared and is protected by enterprise-grade security.',
    },
    {
      q: 'Can I cancel anytime?',
      a: 'Yes! All plans are month-to-month with no long-term contracts. Cancel anytime from your dashboard. Annual plans are non-refundable but you can use the service for the full year.',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="container mx-auto px-4 py-20 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge variant="outline" className="mb-4 border-primary text-primary">
              💎 Premium Financial Transformation Services
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
              Your Investment in
              <span className="text-primary"> Millions</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Transform your credit, save thousands in taxes, and access funding to build real wealth. Choose the plan that matches your goals.
            </p>
            <div className="flex items-center justify-center gap-4 text-sm flex-wrap">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-primary" />
                <span>No long-term contracts</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-primary" />
                <span>Cancel anytime</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-primary" />
                <span>90-day money-back guarantee</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 mb-16">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: index * 0.1 }}
              className="relative"
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                  <Badge className="bg-primary text-background border-0 px-4 py-1">
                    <Star className="w-3 h-3 mr-1" />
                    Most Popular
                  </Badge>
                </div>
              )}
              <Card
                className={`p-6 h-full flex flex-col justify-between ${
                  plan.popular ? 'border-primary bg-primary/5 shadow-lg shadow-primary/20' : ''
                }`}
              >
                <div className="mb-6">
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground">{plan.tagline}</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-4xl font-bold font-mono">${plan.monthlyPrice}</span>
                    <span className="text-muted-foreground">/mo</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    or ${plan.annualPrice}/year <span className="text-primary">(save ${plan.monthlyPrice * 12 - plan.annualPrice})</span>
                  </div>
                  {plan.setupFee > 0 && (
                    <div className="text-xs text-muted-foreground mt-2">
                      + ${plan.setupFee} one-time setup
                    </div>
                  )}
                  {plan.setupFee === 0 && (
                    <div className="text-xs text-primary mt-2 font-semibold">
                      ✨ Setup fee waived ($149 value)
                    </div>
                  )}
                </div>

                <div className="space-y-3 mb-6 flex-1">
                  {plan.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm leading-tight">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="space-y-2">
                  <Button
                    className={`w-full ${plan.popular ? 'bg-primary hover:bg-primary/90' : ''}`}
                    variant={plan.popular ? 'default' : 'outline'}
                    size="lg"
                    asChild
                  >
                    <Link to={ROUTE_PATHS.CONTACT}>
                      Get Started
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                  <Button
                    className="w-full"
                    variant="ghost"
                    size="sm"
                    asChild
                  >
                    <Link to={plan.demoPath}>
                      <Play className="w-4 h-4 mr-2" />
                      View Demo
                    </Link>
                  </Button>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Value Proposition */}
        <div className="mb-16">
          <Card className="p-8 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 border-primary/20">
            <h2 className="text-3xl font-bold text-center mb-8">Why Our Pricing Makes Sense</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-5xl font-bold text-primary mb-2">$47k</div>
                <div className="text-sm text-muted-foreground mb-4">Average Tax Savings Found</div>
                <p className="text-xs text-muted-foreground">
                  Our AI tax scanner finds an average of $47,200 in deductions. That's 10+ years of Wealth Accelerator paid for.
                </p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-primary mb-2">$85k</div>
                <div className="text-sm text-muted-foreground mb-4">Average Funding Access</div>
                <p className="text-xs text-muted-foreground">
                  Our clients get matched with $50k-$150k in business funding. One approval pays for years of service.
                </p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-primary mb-2">+94</div>
                <div className="text-sm text-muted-foreground mb-4">Average Score Increase</div>
                <p className="text-xs text-muted-foreground">
                  Better credit = lower interest rates. A 100-point increase saves $50k+ on a mortgage.
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* ROI Calculator */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Return on Investment</h2>
          <Card className="p-6 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-4 px-4 font-semibold">Benefit</th>
                  <th className="text-center py-4 px-4 font-semibold">Value</th>
                  <th className="text-center py-4 px-4 font-semibold">Your Cost<br/>(Wealth Accelerator)</th>
                  <th className="text-center py-4 px-4 font-semibold bg-primary/10">ROI</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="py-4 px-4">Tax Savings (Year 1)</td>
                  <td className="text-center py-4 px-4 font-mono">$47,200</td>
                  <td className="text-center py-4 px-4 font-mono">$4,764</td>
                  <td className="text-center py-4 px-4 font-mono bg-primary/5 text-primary font-bold">
                    891% ROI
                  </td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-4 px-4">Business Funding Access</td>
                  <td className="text-center py-4 px-4 font-mono">$85,000</td>
                  <td className="text-center py-4 px-4 font-mono">$4,764</td>
                  <td className="text-center py-4 px-4 font-mono bg-primary/5 text-primary font-bold">
                    1,684% ROI
                  </td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-4 px-4">Mortgage Interest Savings<br/><span className="text-xs text-muted-foreground">(100pt increase)</span></td>
                  <td className="text-center py-4 px-4 font-mono">$52,000</td>
                  <td className="text-center py-4 px-4 font-mono">$4,764</td>
                  <td className="text-center py-4 px-4 font-mono bg-primary/5 text-primary font-bold">
                    992% ROI
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-4 font-bold">Total Value</td>
                  <td className="text-center py-4 px-4 font-mono font-bold">$184,200</td>
                  <td className="text-center py-4 px-4 font-mono font-bold">$4,764</td>
                  <td className="text-center py-4 px-4 font-mono bg-primary/10 text-primary font-bold text-lg">
                    3,767% ROI
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="text-xs text-muted-foreground mt-4 text-center">
              * Based on average client results over 12 months. Individual results may vary.
            </p>
          </Card>
        </div>

        {/* FAQs */}
        <div>
          <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...springPresets.gentle, delay: index * 0.1 }}
              >
                <Card className="p-6">
                  <h4 className="font-semibold mb-2">{faq.q}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <Card className="p-12 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 border-primary/20">
            <Zap className="w-12 h-12 text-primary mx-auto mb-4" />
            <h3 className="text-3xl font-bold mb-4">Ready to Transform Your Financial Future?</h3>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join hundreds of clients who have increased their credit scores by 94+ points, saved $47k+ in taxes, and secured $85k+ in business funding.
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <Button size="lg" asChild>
                <Link to={ROUTE_PATHS.CONTACT}>
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Schedule Free Consultation
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to={ROUTE_PATHS.DASHBOARD}>View Live Demo Dashboard</Link>
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
